import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { localeFor, resolveLocale } from './locales';
import type { AppLocaleDef } from './locales';
import { en } from './strings/en';
import { vi } from './strings/vi';
import { zhCN } from './strings/zh-CN';
import { ptBR } from './strings/pt-BR';

/**
 * Every string the UI is allowed to ask for. `en.ts` is the master, so a typo in
 * a `t('...')` call is a compile error and never a blank label on a phone that
 * is three provinces away.
 */
export type StringKey = keyof typeof en;

export interface I18n {
  locale: AppLocaleDef;
  setLocale(tag: AppLocaleDef['tag']): void;
  t(key: StringKey, ...args: (string | number)[]): string;
  /** Locale-aware integer with digit grouping. Port of Android's `formatIntForLocale`. */
  n(value: number): string;
  /** `"1:05"`, or `"1:05:00"` past an hour. Port of Android's `formatSeconds` — see the helper below. */
  seconds(total: number): string;
}

const TABLES: Record<AppLocaleDef['tag'], Record<StringKey, string>> = {
  vi,
  en,
  'zh-CN': zhCN,
  'pt-BR': ptBR,
};

/**
 * The **only** thing this app ever writes to `localStorage`.
 *
 * SPEC §13 Never: the device key lives in the icon's URL and nowhere else —
 * Safari evicts a WebClip's storage on its own schedule, so a cached key both
 * disappears when you need it and outlives a re-provisioning when you do not.
 * A language preference has neither problem: losing it costs one tap, and a
 * stale one is merely the wrong language rather than the wrong identity.
 */
const LANGUAGE_STORAGE_KEY = 'cl.lang';

/**
 * Safari throws on `localStorage` in Private Browsing and when a device has
 * "Block All Cookies" set — a real configuration on locked-down field phones.
 * Both accessors swallow it: the app must start in *some* language, not fail.
 */
function readStoredLanguageTag(): string | null {
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistLanguageTag(tag: AppLocaleDef['tag']): void {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, tag);
  } catch {
    // Nothing to do and nothing to report. The choice still applies for this
    // session; it just will not survive a reload.
  }
}

/**
 * `"{0}"`-style interpolation, the shape Android's `%1$s` args were converted
 * into when the strings were ported.
 *
 * A placeholder with no matching argument is left **as written** rather than
 * replaced with an empty string, because "Round {1} starts in" on screen names
 * the bug, and "Round  starts in" hides it.
 */
function interpolate(template: string, args: readonly (string | number)[]): string {
  if (args.length === 0) return template;
  return template.replace(/\{(\d+)\}/g, (whole: string, index: string) => {
    const arg = args[Number(index)];
    return arg === undefined ? whole : String(arg);
  });
}

/**
 * `0:45`. Port of Android's `Countdown.kt#formatSeconds`, so the same wait is
 * never phrased two ways across the two apps.
 *
 * Not bare seconds: the server may ask for a gap of up to five minutes and
 * nobody reads "300" as five minutes. Negative input is floored rather than
 * rendered, because a late tick must never produce `0:-1`.
 *
 * The hour band is the one place this goes past Android's version, and it is
 * this app's own countdown that needs it. Android's `Countdown.kt` only ever
 * renders the gap *between rounds*, which `clampConfig` bounds to 180s total;
 * `LastCheckBanner` renders the gap to the **next cycle**, and a device
 * configured on an hourly interval would otherwise be told `"60:00"`, which
 * reads as sixty minutes past something rather than as an hour. It lived in
 * that banner as a private helper, one band split across two files from the one
 * countdown format they both spell.
 *
 * Digits stay ungrouped on purpose: this is a clock, and the locale-aware `n()`
 * beside it would put a thousands separator in the middle of one.
 */
function formatSeconds(total: number): string {
  const safe = Math.max(0, Math.floor(total));
  const minutes = Math.floor(safe / 60);
  const ss = String(safe % 60).padStart(2, '0');
  if (minutes < 60) return `${minutes}:${ss}`;
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}:${ss}`;
}

const I18nContext = createContext<I18n | null>(null);

/**
 * Holds the active language for the whole tree.
 *
 * This file imports nothing from `src/components` on purpose — every screen
 * depends on i18n, so an import back the other way would close a cycle and, more
 * practically, would make the string tables impossible to test without a DOM.
 */
export function I18nProvider({ children }: { children: ReactNode }): ReactElement {
  const [tag, setTag] = useState<AppLocaleDef['tag']>(() => {
    // A stored pick wins over the phone's language: somebody deliberately chose
    // it on this device, and a field phone is often set to a language its
    // operator does not read. Everything goes through `resolveLocale`, so a
    // hand-edited or half-written storage value cannot put an unshipped tag
    // into state.
    const stored = readStoredLanguageTag();
    if (stored !== null) return resolveLocale(stored);
    return resolveLocale(typeof navigator === 'undefined' ? null : navigator.language);
  });

  const setLocale = useCallback((next: AppLocaleDef['tag']) => {
    setTag(next);
    persistLanguageTag(next);
  }, []);

  // Keeps `<html lang>` in step so Safari hyphenates, spell-checks and — the one
  // that matters on a shared field device — reads the page aloud in the language
  // it is actually written in.
  useEffect(() => {
    document.documentElement.lang = tag;
  }, [tag]);

  const value = useMemo<I18n>(() => {
    const table = TABLES[tag];
    // Built once per language rather than per call: this runs on every row of a
    // several-hundred-link result list.
    const numbers = new Intl.NumberFormat(tag);
    return {
      locale: localeFor(tag),
      setLocale,
      t: (key, ...args) => interpolate(table[key], args),
      n: (v) => numbers.format(v),
      seconds: formatSeconds,
    };
  }, [tag, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (value === null) {
    // Deliberately loud. Falling back to English here would render a working
    // screen in the wrong language and hide a missing provider until somebody
    // in the field noticed the app had stopped speaking Vietnamese.
    throw new Error('useI18n() called outside <I18nProvider>');
  }
  return value;
}
